import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Upload, MessageSquare, User, CheckCircle, AlertCircle, Shield, Mail, MessageCircle } from 'lucide-react';
import { sendInquiryEmailToAll, sendCustomerConfirmationEmail, InquiryFormData } from '../services/emailService';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { saveSubmissionToSupabase, uploadImageToStorage } from '../services/supabaseService';
import { isSupabaseConfigured } from '../lib/supabase';
import {
  detectSuspiciousContent,
  validateEmail,
  validateSubmissionTiming,
  generateChallenge,
  defaultSecurityConfig,
  getRateLimitData,
  setRateLimitData
} from '../utils/security';

const Inquiry: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  // Daftar kode negara dengan Indonesia dan Australia di atas
  const countryCodes = [
    { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' }
  ];

  const [formData, setFormData] = useState<InquiryFormData>({
    fullName: '',
    email: '',
    countryCode: '+62',
    whatsapp: '',
    dateNeeded: '',
    occasion: '',
    theme: '',
    servings: '',
    message: '',
    images: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [emailSuggestions, setEmailSuggestions] = useState<string[]>([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  // Security states
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [timeSpentOnPage, setTimeSpentOnPage] = useState<number>(0);
  const [formInteractions, setFormInteractions] = useState<number>(0);
  const [honeypotValue, setHoneypotValue] = useState('');
  const [mathChallenge, setMathChallenge] = useState(generateChallenge());
  const [mathAnswer, setMathAnswer] = useState('');

  // Selected products and manual code states
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [manualProductCode, setManualProductCode] = useState('');

  // Security constants
  const { 
    maxSubmitAttempts: MAX_SUBMIT_ATTEMPTS,
    submitCooldown: SUBMIT_COOLDOWN,
    rateLimitWindow: RATE_LIMIT_WINDOW 
  } = defaultSecurityConfig;

  // Daftar domain email populer
  const emailDomains = ['@gmail.com', '@outlook.com', '@yahoo.com', '@icloud.com'];

  const occasions = [
    t('inquiry.occasions.birthday'),
    t('inquiry.occasions.wedding'),
    t('inquiry.occasions.anniversary'),
    t('inquiry.occasions.babyShower'),
    t('inquiry.occasions.corporate'),
    t('inquiry.occasions.graduation'),
    t('inquiry.occasions.other')
  ];

  // Security useEffect
  useEffect(() => {
    const startTime = Date.now();

    // Track time spent on page
    const timeTracker = setInterval(() => {
      setTimeSpentOnPage(Date.now() - startTime);
    }, 1000);

    // Check localStorage for previous submission attempts
    const rateLimitData = getRateLimitData('inquiryRateLimit');

    if (rateLimitData) {
      const { attempts, lastAttempt } = rateLimitData;
      const timeSinceLastAttempt = Date.now() - lastAttempt;

      if (attempts >= MAX_SUBMIT_ATTEMPTS && timeSinceLastAttempt < SUBMIT_COOLDOWN) {
        setIsBlocked(true);
        setSubmitAttempts(attempts);
        setLastSubmitTime(lastAttempt);
      } else if (timeSinceLastAttempt >= SUBMIT_COOLDOWN) {
        localStorage.removeItem('inquiryRateLimit');
      }
    }

    return () => clearInterval(timeTracker);
  }, [MAX_SUBMIT_ATTEMPTS, SUBMIT_COOLDOWN]);

  // Load selected products from localStorage
  useEffect(() => {
    const storedProducts = localStorage.getItem('selectedProducts');
    if (storedProducts) {
      try {
        const products = JSON.parse(storedProducts);
        setSelectedProducts(products);
      } catch (error) {
        console.error('Error parsing selected products:', error);
      }
    }
  }, []);

  // Security validation function
  const validateSecurity = (): { isValid: boolean; message: string } => {
    if (honeypotValue !== '') {
      console.warn('Bot detected: Honeypot filled');
      return { isValid: false, message: 'Security validation failed' };
    }

    console.log('Math validation:', { 
      userAnswer: mathAnswer, 
      correctAnswer: mathChallenge.answer, 
      userParsed: parseInt(mathAnswer), 
      correctParsed: parseInt(mathChallenge.answer),
      isMatch: parseInt(mathAnswer) === parseInt(mathChallenge.answer)
    });

    if (parseInt(mathAnswer) !== parseInt(mathChallenge.answer)) {
      return { isValid: false, message: `Please solve the math challenge correctly. ${mathChallenge.challenge}` };
    }

    const timingValidation = validateSubmissionTiming(timeSpentOnPage, formInteractions, defaultSecurityConfig);
    if (!timingValidation.isValid) {
      return { isValid: false, message: timingValidation.reason || 'Invalid submission timing' };
    }

    if (detectSuspiciousContent(formData)) {
      return { isValid: false, message: 'Content validation failed' };
    }

    if (!validateEmail(formData.email)) {
      return { isValid: false, message: 'Please provide a valid email address' };
    }

    const currentTime = Date.now();
    if (lastSubmitTime > 0 && (currentTime - lastSubmitTime) < RATE_LIMIT_WINDOW) {
      return { isValid: false, message: 'Please wait before submitting again' };
    }

    if (isBlocked) {
      const timeLeft = Math.ceil((SUBMIT_COOLDOWN - (currentTime - lastSubmitTime)) / 60000);
      return { isValid: false, message: `Too many attempts. Please try again in ${timeLeft} minutes` };
    }

    return { isValid: true, message: '' };
  };

  const trackInteraction = () => {
    setFormInteractions(prev => prev + 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    trackInteraction();
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'email') {
      handleEmailChange(value);
    }
  };

  const handleEmailChange = (value: string) => {
    if (value.includes('@')) {
      setShowEmailSuggestions(false);
      setEmailSuggestions([]);
      setSelectedSuggestionIndex(-1);
    } else if (value.length > 0) {
      const suggestions = emailDomains.map(domain => value + domain);
      setEmailSuggestions(suggestions);
      setShowEmailSuggestions(true);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowEmailSuggestions(false);
      setEmailSuggestions([]);
      setSelectedSuggestionIndex(-1);
    }
  };

  const selectEmailSuggestion = (suggestion: string) => {
    setFormData(prev => ({
      ...prev,
      email: suggestion
    }));
    setShowEmailSuggestions(false);
    setEmailSuggestions([]);
    setSelectedSuggestionIndex(-1);
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showEmailSuggestions || emailSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < emailSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : emailSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          selectEmailSuggestion(emailSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowEmailSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({
        ...prev,
        images: Array.from(e.target.files!)
      }));
    }
  };

  const handleSend = async (method: 'email' | 'whatsapp') => {
    const securityCheck = validateSecurity();
    if (!securityCheck.isValid) {
      setSubmitStatus('error');
      alert(securityCheck.message);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // 1. If customer uploaded reference images, upload them to Supabase Storage
      let imageUrls: string[] = [];
      if (formData.images && formData.images.length > 0 && isSupabaseConfigured) {
        for (const file of formData.images) {
          try {
            const url = await uploadImageToStorage(file, 'gallery-images');
            if (url) imageUrls.push(url);
          } catch (uploadErr) {
            console.error('Failed to upload customer design image:', uploadErr);
          }
        }
      }

      const currentTime = Date.now();
      const rateLimitData = getRateLimitData('inquiryRateLimit') || { attempts: 0, lastAttempt: 0 };
      const newAttempts = rateLimitData.attempts + 1;

      setRateLimitData('inquiryRateLimit', {
        attempts: newAttempts,
        lastAttempt: currentTime
      });

      setSubmitAttempts(newAttempts);
      setLastSubmitTime(currentTime);

      if (newAttempts >= MAX_SUBMIT_ATTEMPTS) {
        setIsBlocked(true);
      }

      // Design links block for email / whatsapp
      const imagesText = imageUrls.length > 0
        ? `Uploaded Design References:\n${imageUrls.map((url, idx) => `Link ${idx + 1}: ${url}`).join('\n')}\n\n`
        : '';

      if (method === 'email') {
        // Determine recipient based on language
        const recipientEmail = currentLanguage === 'en' ? 'Tony@tncakesbali.com' : 'Nila@tncakesbali.com';

        // Create summary for email
        const selectedCodes = selectedProducts.map(p => p.code).join(', ');
        const manualCodes = manualProductCode ? `, ${manualProductCode}` : '';
        const allCodes = selectedCodes || manualProductCode ? `Product Codes: ${selectedCodes}${manualCodes}\n\n` : '';

        const emailBody = `
New Cake Inquiry

Customer Details:
Name: ${formData.fullName}
Email: ${formData.email}
WhatsApp: ${formData.countryCode} ${formData.whatsapp}

${allCodes}${imagesText}Cake Details:
Date Needed: ${formData.dateNeeded}
Occasion: ${formData.occasion}
Theme: ${formData.theme || 'Not specified'}
Servings: ${formData.servings || 'Not specified'}
Message: ${formData.message || 'No additional message'}

Submitted: ${new Date().toLocaleString()}
        `.trim();

        // Log submission to Supabase + localStorage FIRST, so the inquiry is
        // always visible in the admin dashboard even if EmailJS fails.
        {
          const submission = {
            id: String(Date.now()),
            fullName: formData.fullName,
            email: formData.email,
            countryCode: formData.countryCode,
            whatsapp: formData.whatsapp,
            dateNeeded: formData.dateNeeded,
            occasion: formData.occasion,
            theme: formData.theme || '',
            servings: formData.servings || '',
            message: formData.message || '',
            productCodes: `${selectedProducts.map(p => p.code).join(', ')}${manualProductCode ? (selectedProducts.length ? ', ' : '') + manualProductCode : ''}`,
            method: 'email' as const,
            submittedAt: new Date().toISOString()
          };
          // Save to Supabase (cloud) — so admin can see from any device
          saveSubmissionToSupabase(submission).catch(console.error);
          // Also save to localStorage as local fallback
          const submissions = JSON.parse(localStorage.getItem('tncakes_form_submissions') || '[]');
          submissions.unshift(submission);
          localStorage.setItem('tncakes_form_submissions', JSON.stringify(submissions));
        }

        // Send a copy to the primary recipient (by language) + ALWAYS_NOTIFY_EMAILS:
        // tony@tncakesbali.com, admin@tncakesbali.com, antonio.pati@outlook.com,
        // tncakesdev@gmail.com. Duplicates are removed automatically.
        const { success: emailSent } = await sendInquiryEmailToAll({
          ...formData,
          message: emailBody
        }, recipientEmail, currentLanguage);

        if (emailSent) {
          await sendCustomerConfirmationEmail(formData.email, formData.fullName);
          setSubmitStatus('success');
        } else {
          setSubmitStatus('error');
        }
      } else if (method === 'whatsapp') {
        // Determine recipient based on language
        const recipientNumber = currentLanguage === 'en' ? '+6281234567890' : '+6281234567891'; // Replace with actual numbers

        // Create WhatsApp message
        const selectedCodes = selectedProducts.map(p => p.code).join(', ');
        const manualCodes = manualProductCode ? `, ${manualProductCode}` : '';
        const allCodes = selectedCodes || manualProductCode ? `Product Codes: ${selectedCodes}${manualCodes}\n\n` : '';
        const waImagesText = imageUrls.length > 0
          ? `🖼️ Design References:\n${imageUrls.map((url, idx) => `Link ${idx + 1}: ${url}`).join('\n')}\n\n`
          : '';

        const whatsappMessage = `
*New Cake Inquiry*

👤 *Customer Details:*
Name: ${formData.fullName}
Email: ${formData.email}
WhatsApp: ${formData.countryCode} ${formData.whatsapp}

${allCodes}${waImagesText}🎂 *Cake Details:*
📅 Date Needed: ${formData.dateNeeded}
🎉 Occasion: ${formData.occasion}
🎨 Theme: ${formData.theme || 'Not specified'}
🍽️ Servings: ${formData.servings || 'Not specified'}
💬 Message: ${formData.message || 'No additional message'}

📝 Submitted: ${new Date().toLocaleString()}
        `.trim();

        // Encode message for WhatsApp URL
        const encodedMessage = encodeURIComponent(whatsappMessage);
        const whatsappUrl = `https://wa.me/${recipientNumber.replace('+', '')}?text=${encodedMessage}`;

        // Open WhatsApp
        window.open(whatsappUrl, '_blank');

        // Also email a copy of this WhatsApp inquiry to every ALWAYS_NOTIFY_EMAILS
        // address (tony@, admin@, antonio.pati@outlook.com, tncakesdev@gmail.com).
        // Fire-and-forget so it never blocks the WhatsApp flow.
        sendInquiryEmailToAll({
          ...formData,
          message: `[Inquiry submitted via WhatsApp]\n\n${whatsappMessage}`
        }, null, currentLanguage).catch(console.error);

        // Log submission to Supabase + localStorage fallback
        const submission = {
          id: String(Date.now()),
          fullName: formData.fullName,
          email: formData.email,
          countryCode: formData.countryCode,
          whatsapp: formData.whatsapp,
          dateNeeded: formData.dateNeeded,
          occasion: formData.occasion,
          theme: formData.theme || '',
          servings: formData.servings || '',
          message: formData.message || '',
          productCodes: `${selectedProducts.map(p => p.code).join(', ')}${manualProductCode ? (selectedProducts.length ? ', ' : '') + manualProductCode : ''}`,
          method: 'whatsapp' as const,
          submittedAt: new Date().toISOString()
        };
        // Save to Supabase (cloud) — so admin can see from any device
        saveSubmissionToSupabase(submission).catch(console.error);
        // Also save to localStorage as local fallback
        const submissions = JSON.parse(localStorage.getItem('tncakes_form_submissions') || '[]');
        submissions.unshift(submission);
        localStorage.setItem('tncakes_form_submissions', JSON.stringify(submissions));

        // Show success message and reset form
        setSubmitStatus('success');
        
        setFormData({
          fullName: '',
          email: '',
          countryCode: '+62',
          whatsapp: '',
          dateNeeded: '',
          occasion: '',
          theme: '',
          servings: '',
          message: '',
          images: null
        });

        setSelectedProducts([]);
        setManualProductCode('');
        setFormInteractions(0);
        setHoneypotValue('');
        setMathChallenge(generateChallenge());
        setMathAnswer('');

        const fileInput = document.getElementById('image-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';

        // Clear localStorage
        localStorage.removeItem('selectedProducts');
      }
    } catch (error) {
      console.error('Error sending inquiry:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSend('email');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-premium-50">
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-r from-burgundy-100 via-cream-100 to-premium-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
              {t('inquiry.title')}
            </h1>
            <div className="w-24 h-1 gold-gradient rounded-full mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 leading-relaxed">
              {t('inquiry.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content - Side by Side Layout */}
      <section className="py-12">
        <div className="container mx-auto px-3 sm:px-6 lg:px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12 items-start">
              
              {/* Left Column - How It Works */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-8"
              >
                <div className="text-center lg:text-left">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
                    {t('inquiry.howItWorks.title')}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-8">
                    {t('inquiry.howItWorks.subtitle')}
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Step 1 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-burgundy-500 to-burgundy-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      01
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {t('inquiry.howItWorks.step1.title')}
                      </h3>
                      <p className="text-gray-600">
                        {t('inquiry.howItWorks.step1.description')}
                      </p>
                    </div>
                  </motion.div>

                  {/* Step 2 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-burgundy-500 to-burgundy-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      02
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {t('inquiry.howItWorks.step2.title')}
                      </h3>
                      <p className="text-gray-600">
                        {t('inquiry.howItWorks.step2.description')}
                      </p>
                    </div>
                  </motion.div>

                  {/* Step 3 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-burgundy-500 to-burgundy-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      03
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {t('inquiry.howItWorks.step3.title')}
                      </h3>
                      <p className="text-gray-600">
                        {t('inquiry.howItWorks.step3.description')}
                      </p>
                    </div>
                  </motion.div>

                  {/* Step 4 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-burgundy-500 to-burgundy-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      04
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {t('inquiry.howItWorks.step4.title')}
                      </h3>
                      <p className="text-gray-600">
                        {t('inquiry.howItWorks.step4.description')}
                      </p>
                    </div>
                  </motion.div>
                </div>

                {/* Trust Indicators */}
                <div className="bg-gradient-to-r from-burgundy-50 to-premium-50 rounded-2xl p-6 border border-burgundy-200">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-yellow-500">5★★★★★</div>
                      <div className="text-sm text-gray-600">{t('inquiry.stats.rating')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-burgundy-600">Same Day</div>
                      <div className="text-sm text-gray-600">{t('inquiry.stats.response')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-burgundy-600">5,000+</div>
                      <div className="text-sm text-gray-600">{t('inquiry.stats.cakes')}</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right Column - Form */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="lg:sticky lg:top-8"
              >
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 space-y-6 transition-all duration-300 hover:shadow-2xl mx-2 sm:mx-0">
                  {/* Form Title */}
                  <div className="text-center">
                    <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                      {t('inquiry.formTitle')}
                    </h2>
                    <p className="text-gray-600">
                      {t('inquiry.formSubtitle')}
                    </p>
                  </div>

                  {/* Honeypot field */}
                  <input
                    type="text"
                    name="website"
                    value={honeypotValue}
                    onChange={(e) => setHoneypotValue(e.target.value)}
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      opacity: 0,
                      height: 0,
                      width: 0,
                      zIndex: -1
                    }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <User className="w-5 h-5 text-burgundy-600" />
                      {t('inquiry.personalInfo')}
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('inquiry.fullName')} *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        onFocus={trackInteraction}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300"
                        placeholder={t('inquiry.placeholders.fullName')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('inquiry.email')} *
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onKeyDown={handleEmailKeyDown}
                          onFocus={() => {
                            trackInteraction();
                            if (formData.email && !formData.email.includes('@')) {
                              handleEmailChange(formData.email);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              setShowEmailSuggestions(false);
                              setSelectedSuggestionIndex(-1);
                            }, 200);
                          }}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300"
                          placeholder={t('inquiry.placeholders.email')}
                        />

                        {/* Email Suggestions */}
                        {showEmailSuggestions && emailSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                            {emailSuggestions.map((suggestion, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => selectEmailSuggestion(suggestion)}
                                className={`w-full text-left px-4 py-2 transition-colors duration-200 first:rounded-t-xl last:rounded-b-xl ${
                                  index === selectedSuggestionIndex
                                    ? 'bg-burgundy-100 text-burgundy-700'
                                    : 'hover:bg-burgundy-50 hover:text-burgundy-600'
                                }`}
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('inquiry.whatsapp')} *
                      </label>
                      <div className="flex gap-2 w-full">
                        <select
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={handleInputChange}
                          className="px-2 sm:px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300 bg-white min-w-0 flex-shrink-0 text-sm sm:text-base"
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          name="whatsapp"
                          value={formData.whatsapp}
                          onChange={handleInputChange}
                          onFocus={trackInteraction}
                          required
                          className="flex-1 min-w-0 px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                          placeholder={t('inquiry.placeholders.whatsapp')}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Selected Products */}
                  {selectedProducts.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-burgundy-600" />
                        Selected Products
                      </h3>
                      <div className="bg-gradient-to-r from-burgundy-50 to-pink-50 p-4 rounded-xl border border-burgundy-200">
                        <div className="space-y-3">
                          {selectedProducts.map((product, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                              <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                <img
                                  src={product.image}
                                  alt={product.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">
                                  {product.title}
                                </p>
                                <p className="text-xs text-gray-600">
                                  Code: {product.code}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Manual Product Code Input */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-burgundy-600" />
                      Product Code (Optional)
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enter Product Code
                      </label>
                      <input
                        type="text"
                        value={manualProductCode}
                        onChange={(e) => setManualProductCode(e.target.value)}
                        onFocus={trackInteraction}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300"
                        placeholder="e.g., TNC-001, TNC-002"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        If you have a specific product code from our gallery, enter it here
                      </p>
                    </div>
                  </div>

                  {/* Cake Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-burgundy-600" />
                      {t('inquiry.cakeDetails')}
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('inquiry.dateNeeded')} *
                      </label>
                      <input
                        type="date"
                        name="dateNeeded"
                        value={formData.dateNeeded}
                        onChange={handleInputChange}
                        onFocus={trackInteraction}
                        required
                        className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('inquiry.occasion')} *
                      </label>
                      <select
                        name="occasion"
                        value={formData.occasion}
                        onChange={handleInputChange}
                        onFocus={trackInteraction}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300 bg-white"
                      >
                        <option value="">{t('inquiry.placeholders.occasion')}</option>
                        {occasions.map((occasion, index) => (
                          <option key={index} value={occasion}>
                            {occasion}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('inquiry.servings')}
                      </label>
                      <input
                        type="number"
                        name="servings"
                        value={formData.servings}
                        onChange={handleInputChange}
                        onFocus={trackInteraction}
                        min="1"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300"
                        placeholder={t('inquiry.placeholders.servings')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('inquiry.theme')}
                      </label>
                      <textarea
                        name="theme"
                        value={formData.theme}
                        onChange={handleInputChange}
                        onFocus={trackInteraction}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300 resize-vertical"
                        placeholder={t('inquiry.placeholders.theme')}
                      />
                    </div>


                  </div>

                  {/* File Upload */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Upload className="w-5 h-5 text-burgundy-600" />
                      {t('inquiry.uploadImages')}
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('inquiry.uploadImages')} ({t('inquiry.optional')})
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gold-400 transition-colors">
                        <input
                          type="file"
                          id="image-upload"
                          multiple
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="image-upload"
                          className="cursor-pointer flex flex-col items-center gap-2"
                        >
                          <Upload className="w-8 h-8 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {t('inquiry.uploadText')}
                          </span>
                        </label>
                        {formData.images && formData.images.length > 0 && (
                          <p className="text-sm text-burgundy-600 mt-2">
                            {formData.images.length} {t('inquiry.filesSelected')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-green-700">{t('inquiry.successTitle')}</span>
                        <p className="text-sm text-green-600 mt-1">
                          {t('inquiry.successMessage')}
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-red-700">{t('inquiry.errorTitle')}</span>
                        <p className="text-sm text-red-600 mt-1">
                          {t('inquiry.errorMessage')}
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Security Status */}
                  {isBlocked && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <Shield className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-red-700">Security Protection Active</span>
                        <p className="text-sm text-red-600 mt-1">
                          Too many submission attempts ({submitAttempts}/{MAX_SUBMIT_ATTEMPTS}). Please wait {Math.ceil((SUBMIT_COOLDOWN - (Date.now() - lastSubmitTime)) / 60000)} minutes before trying again.
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Math Challenge */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('inquiry.securityChallenge')}
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="bg-gray-100 px-4 py-3 rounded-xl font-semibold text-gray-900">
                        {mathChallenge.challenge}
                      </div>
                      <span className="text-gray-500">=</span>
                      <input
                        type="number"
                        value={mathAnswer}
                        onChange={(e) => {
                          setMathAnswer(e.target.value);
                          trackInteraction();
                        }}
                        onFocus={trackInteraction}
                        required
                        className="w-32 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-300"
                        placeholder={t('inquiry.answerPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Security Indicator */}
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Shield className="w-4 h-4" />
                    <span>{t('inquiry.securityProtected')}</span>
                  </div>

                  {/* Send Options */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Send className="w-5 h-5 text-burgundy-600" />
                      Send Options
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <motion.button
                        type="button"
                        onClick={() => handleSend('email')}
                        disabled={isSubmitting || isBlocked}
                        whileHover={{ scale: (isSubmitting || isBlocked) ? 1 : 1.05 }}
                        whileTap={{ scale: (isSubmitting || isBlocked) ? 1 : 0.95 }}
                        className={`btn-premium text-base px-6 py-4 inline-flex items-center justify-center gap-3 ${
                          (isSubmitting || isBlocked) ? 'opacity-75 cursor-not-allowed' : ''
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            Send as Email
                          </>
                        )}
                      </motion.button>

                      <motion.button
                        type="button"
                        onClick={() => handleSend('whatsapp')}
                        disabled={isSubmitting || isBlocked}
                        whileHover={{ scale: (isSubmitting || isBlocked) ? 1 : 1.05 }}
                        whileTap={{ scale: (isSubmitting || isBlocked) ? 1 : 0.95 }}
                        className={`bg-green-600 hover:bg-green-700 text-white text-base px-6 py-4 inline-flex items-center justify-center gap-3 rounded-xl font-semibold transition-all duration-300 ${
                          (isSubmitting || isBlocked) ? 'opacity-75 cursor-not-allowed' : ''
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <MessageCircle className="w-4 h-4" />
                            Send as WhatsApp
                          </>
                        )}
                      </motion.button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 text-center">
                    {isSubmitting
                      ? 'Sending your inquiry...'
                      : 'Choose how you\'d like to send your inquiry'
                    }
                  </p>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Inquiry;
